import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import {
  UsersRound, Palette, Truck, CalendarRange, DollarSign,
  ArrowRight, LayoutDashboard, Settings, ScanLine, Gauge, FileText, GitBranch, Handshake, BookOpen, BarChart3,
} from "lucide-react";

/**
 * Display America Event Operations Platform · Workspace Home.
 * Five operational workspaces + top-level KPI strip.
 */
const WORKSPACES = [
  {
    slug: "registration", label: "Registration", icon: UsersRound,
    description: "Attendees, payments, stations, emails, check-in.",
    testId: "ws-registration",
  },
  {
    slug: "production", label: "Production", icon: Palette,
    description: "Logos, artwork, proofs, approvals, printing, packing, installation, production status.",
    testId: "ws-production",
  },
  {
    slug: "logistics", label: "Logistics", icon: Truck,
    description: "Freight, electrical, furniture, labor, warehouse, install, dismantle, return shipping.",
    testId: "ws-logistics",
  },
  {
    slug: "event-ops", label: "Event Operations", icon: CalendarRange,
    description: "Speakers, schedule, matchmaking, documents, staff, readiness, live status.",
    testId: "ws-event-ops",
  },
  {
    slug: "finance", label: "Finance", icon: DollarSign,
    description: "Supplier revenue, production costs, labor, shipping, travel, gross profit.",
    testId: "ws-finance",
  },
];

export default function AdminWorkspaces() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [kpis, setKpis] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/login"); return; }
    api.get("/admin/workspace-kpis").then(({ data }) => setKpis(data)).catch(() => {});
  }, [user, loading, nav]);

  if (loading) return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white border-b border-white/10">
        <div className="container-summit py-10 md:py-14">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-white/60"><span className="accent-rule" />Display America · Event Operations</div>
              <h1 className="serif text-4xl sm:text-5xl mt-3">Operate the entire summit from one platform.</h1>
              <p className="mt-3 max-w-2xl text-white/70 text-sm">
                Five operational workspaces — a single source of truth before, during, and after the event.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/command-center" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-command-center">
                <LayoutDashboard size={12} /> Command Center
              </Link>
              <Link to="/event-settings" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-event-settings">
                <Settings size={12} /> Event Settings
              </Link>
              <Link to="/checkin" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-checkin">
                <ScanLine size={12} /> Check-In
              </Link>
              <Link to="/admin/documents" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-documents">
                <FileText size={12} /> Documents
              </Link>
              <Link to="/admin/operations" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-operations">
                <GitBranch size={12} /> Operations Matrix
              </Link>
              <Link to="/admin/matchmaking" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-matchmaking">
                <Handshake size={12} /> Matchmaking Console
              </Link>
              <Link to="/admin/matchmaking/runbook" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-runbook">
                <BookOpen size={12} /> Matchmaking Runbook
              </Link>
              <Link to="/admin/program" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-program">
                <BarChart3 size={12} /> Program Tracker
              </Link>
              <Link to="/admin/survey" className="btn-outline-black h-10 px-4 text-xs bg-transparent text-white border-white/30 hover:border-white" data-testid="ah-link-survey">
                <BarChart3 size={12} /> Post-Event Survey
              </Link>
            </div>
          </div>

          {/* KPI strip */}
          {kpis && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10" data-testid="ah-kpis">
              <Kpi label="Registered" value={kpis.registered} />
              <Kpi label="Paid Suppliers" value={kpis.paid_prime} />
              <Kpi label="Proofs Pending" value={kpis.proofs_pending} accent={kpis.proofs_pending > 0} />
              <Kpi label="Open Logistics" value={kpis.logistics_open} accent={kpis.logistics_open > 0} />
              <Kpi label={kpis.days_to_event != null ? "Days to Event" : "Event Date"}
                   value={kpis.days_to_event != null ? kpis.days_to_event : "—"} />
            </div>
          )}
        </div>
      </section>

      {/* Launch Readiness banner + Workspace grid */}
      <section className="container-summit py-14">
        <Link to="/admin/launch-readiness" data-testid="ah-launch-readiness"
              className="group block border border-black bg-black text-white p-7 md:p-8 mb-8 hover:bg-[#A39171] hover:border-[#A39171] transition-colors">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 border border-current flex items-center justify-center">
                <Gauge size={20} strokeWidth={1.4} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Executive · Pre-launch</div>
                <div className="serif text-3xl leading-tight mt-1">Launch Readiness</div>
                <div className="text-sm opacity-80 mt-1">Single go/no-go dashboard for the entire event.</div>
              </div>
            </div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium">
              Open Dashboard <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </div>
          </div>
        </Link>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WORKSPACES.map((w) => (
            <Link key={w.slug} to={`/admin/${w.slug}`} data-testid={w.testId}
                  className="group border border-black bg-white p-7 hover:bg-black hover:text-white transition-colors flex flex-col">
              <div className="h-11 w-11 border border-current flex items-center justify-center mb-6">
                <w.icon size={18} strokeWidth={1.4} />
              </div>
              <div className="serif text-3xl leading-tight">{w.label}</div>
              <p className="mt-3 text-sm opacity-80 leading-relaxed flex-1">{w.description}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-medium">
                Open Workspace <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`px-5 py-4 bg-black ${accent ? "text-[#A39171]" : "text-white"}`}>
      <div className="serif text-3xl tabular-nums">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-[0.22em] mt-1 text-white/60">{label}</div>
    </div>
  );
}
